/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useContext } from 'react';
import { createStore, useStore } from 'zustand';
import type {
  FieldState,
  FieldError,
  FieldValidation,
  FieldData,
  FormSetters,
  HandleChangeValue,
  HandleChangeError,
  HandleChangeState,
  HandleChangeStoreType,
  HandleChangeValuesType,
} from '@shared/types';
import { useShallow } from "zustand/react/shallow";
import createFormStore from "./store";

const FALLBACK_STORE = createStore(() => ({
  formValues: {},
  formStates: {},
  formErrors: {},
  formValidations: {},
}));

const { Provider } = createFormStore;
export const { useStore: useFormStore } = createFormStore;
export { Provider };

/**
 * useFormStoreFieldData function.
 *
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export const useFormStoreFieldData = (fieldName: string): FieldData => {
  return useFormStore(
    useShallow((state) => ({
      value: state.formValues[fieldName],
      error: state.formErrors[fieldName],
      state: state.formStates[fieldName],
      validation: state.formValidations[fieldName],
    }))
  ) satisfies FieldData;
};

/**
 * useFormStoreFieldValue function.
 *
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export const useFormStoreFieldValue = (fieldName: string) => {
  return useFormStore((state) => state.formValues[fieldName]);
};

/**
 * useFormStoreFieldError function.
 *
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export const useFormStoreFieldError = (fieldName: string): FieldError => {
  const store = useContext(createFormStore.Context);
  return useStore(
    store ?? FALLBACK_STORE,
    (state: any) => state.formErrors[fieldName]
  ) satisfies FieldError;
};

/**
 * useFormStoreFieldState function.
 *
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export const useFormStoreFieldState = (fieldName: string): FieldState => {
  const store = useContext(createFormStore.Context);
  return (useStore(
    store ?? FALLBACK_STORE,
    (state: any) => state.formStates[fieldName]
  ) ?? {}) satisfies FieldState;
};

/**
 * useFormStoreFieldValidation function.
 *
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export const useFormStoreFieldValidation = (
  fieldName: string
): FieldValidation => {
  return useFormStore(
    (state) => state.formValidations[fieldName]
  ) satisfies FieldValidation;
};

/**
 * useFormStoreSetters function.
 *
 * @returns {*} Return value description
 */
export const useFormStoreSetters = (): FormSetters => {
  return useFormStore(
    useShallow((state) => ({
      set: state.set,
      updateValues: state.updateValues,
      setFieldValues: state.setFieldValues,
      setFieldValue: state.setFieldValue,
      setFieldState: state.setFieldState,
      setFieldError: state.setFieldError,
    }))
  ) as {
    set: HandleChangeStoreType;
    updateValues: HandleChangeValuesType;
    setFieldValues: HandleChangeValuesType;
    setFieldValue: HandleChangeValue;
    setFieldState: HandleChangeState;
    setFieldError: HandleChangeError;
  };
};

export const FormConsumer: ComponentFn<typeof useFormStore> = ({
  children,
}) => {
  return children(useFormStore);
};
